import type { StaticPageSectionData } from '../types';
import { toStaticPageSectionId, toStaticPageSlug } from '../utils/staticPageIds';

type StaticPageNavigationProps = {
  title: string;
  intro?: string;
  sections: Pick<StaticPageSectionData, 'title'>[];
  'data-testid'?: string;
};

/**
 * In-page contents index for long static documents.
 *
 * Builds hash links from section titles so readers can jump to matching
 * {@link StaticPageSection} headings. Use when a page has a table of contents;
 * omit it on shorter pages that do not need navigation.
 *
 * Links are plain anchors: same-page hash jumps need no router, and `next/link`
 * would request the route from the server on every click. Keyboard focus comes
 * from the theme's base `*:focus-visible` rule.
 *
 * @param title - Contents heading
 * @param intro - Optional copy above the link list
 * @param sections - Section titles used to generate heading anchors
 */
function StaticPageNavigation({ title, intro, sections, 'data-testid': dataTestId = 'static-page-navigation' }: StaticPageNavigationProps) {
  return (
    <section className="flex flex-col gap-4" data-testid={dataTestId}>
      <h2 className="display-s">{title}</h2>
      <div className="flex flex-col justify-between gap-10">
        {intro ? <p>{intro}</p> : null}
        <ol className="ol-navigation flex flex-col gap-4">
          {sections.map(section => (
            <li key={toStaticPageSectionId(section.title)}>
              <a
                href={`#${toStaticPageSectionId(section.title)}`}
                className="link-label text-foreground hover:text-foreground-hover no-underline"
                data-testid={`static-page-nav-${toStaticPageSlug(section.title)}`}
              >
                {section.title}
              </a>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

export { StaticPageNavigation, type StaticPageNavigationProps };
